import { memo, useCallback } from 'react';
import { useTranslation } from '@bonanza-social/i18n';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { useNavigation } from '@bonanza-social/navigation';
import { formatDate } from '@bonanza-social/toolkit/utils';
import { selectStatus, statusChanged } from '../../state/status/status.slice';
import { openStatusProgressInfo } from '../../utils/status';
import { StatusRetentionProps } from './StatusRetention.types';
import StatusRetentionAnimation from './StatusRetentionAnimation/StatusRetentionAnimation';
import StatusChange from '../StatusChange/StatusChange';

function StatusRetention({ onButtonPress, ...rest }: StatusRetentionProps) {
  const [t] = useTranslation('status');
  const navigation = useNavigation();
  const status = useSelector(selectStatus);
  const context = { context: status?.playerStatus };
  const dispatch = useDispatch();

  const renderAnimation = useCallback(
    (onAnimationEnd: () => void) =>
      status && (
        <StatusRetentionAnimation
          currentStatus={status.playerStatus}
          nextStatus={status.nextPlayerStatus}
          totalLoyaltyPoints={status.totalLoyaltyPoints}
          promotionNumberOfBoxes={status.promotionNumberOfBoxes}
          keepStatusNumberOfBoxes={status.keepStatusNumberOfBoxes}
          nextStatusRewardAmount={status.nextStatusRewardAmount}
          onAnimationEnd={onAnimationEnd}
        />
      ),
    [status],
  );

  const handleButtonPress = useCallback(() => {
    if (!status) {
      return;
    }

    onButtonPress();
    dispatch(statusChanged());
    openStatusProgressInfo(status, navigation);
  }, [status, onButtonPress, navigation, dispatch]);

  if (!status) {
    return;
  }

  return (
    <StatusChange
      heading={t('statusRetentionHeading', context)}
      subheading={t('statusRetentionSubheading', context)}
      description={t('statusRetentionDescription', {
        nextStatus: status.nextPlayerStatus,
        date: formatDate(status.expiryDate || '', 'MMM DD, YYYY @ ha'),
        ...context,
      })}
      buttonText={t('statusRetentionButton', context)}
      renderAnimation={renderAnimation}
      onButtonPress={handleButtonPress}
      {...rest}
    />
  );
}

export default memo(StatusRetention);
